import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoChevronDown,
  IoChevronUp,
  IoCloseCircleOutline,
  IoAddCircleOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';
import { db } from '../../firebase/config';
import {
  collection, query, where, getDocs, doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { hasReviewForRequest } from '../../services/reviewService';
import { createComplaint } from '../../services/complaintService';
import { SERVICE_CATEGORIES } from '../../services/customerFormService';
import RatingModal from './components/RatingModal';

const STATUS_MAP = {
  unread: { label: '대기', bg: '#FEF3C7', text: '#92400E', desc: '접수대기' },
  read: { label: '확인', bg: '#DBEAFE', text: '#1D4ED8', desc: '접수확인' },
  assigned: { label: '배정', bg: '#D1FAE5', text: '#065F46', desc: '업체 배정완료' },
  cancelled: { label: '취소됨', bg: '#FEE2E2', text: '#991B1B', desc: '취소됨' },
  completed: { label: '완료', bg: '#D1FAE5', text: '#065F46', desc: '서비스 완료' },
  settlement_check: { label: '정산확인', bg: '#E0E7FF', text: '#3730A3', desc: '정산 확인 중' },
  settlement_done: { label: '정산완료', bg: '#D1FAE5', text: '#065F46', desc: '정산 완료' },
};

const CANCEL_REASONS = [
  { key: 'change_of_mind', label: '변심' },
  { key: 'price', label: '가격 불만' },
  { key: 'partner', label: '업체 불만' },
  { key: 'schedule', label: '일정 변경' },
  { key: 'etc', label: '기타' },
];

const SVC_META = SERVICE_CATEGORIES.reduce((acc, s) => {
  acc[s.key] = { label: s.label, emoji: s.emoji };
  return acc;
}, {});

const maskPhone = (phone) => {
  if (!phone || phone.length < 7) return phone;
  return phone.slice(0, 3) + '****' + phone.slice(-4);
};

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = h < 12 ? '오전' : '오후';
  const h12 = h % 12 || 12;
  return `${y}년 ${m}월 ${day}일 ${ampm} ${h12}:${min}`;
};

const formatDateShort = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const CANCEL_REASON_LABEL = CANCEL_REASONS.reduce((acc, r) => {
  acc[r.key] = r.label;
  return acc;
}, {});

const CustomerMyPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const [ratingTarget, setRatingTarget] = useState(null);

  const [accordionOpen, setAccordionOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const handleSearch = async () => {
    if (!name.trim() || !phone.trim()) return;
    setLoading(true);
    setSearched(true);
    setCustomer(null);
    setRequests([]);

    try {
      const phoneDigits = phone.replace(/\D/g, '');
      const nameTrim = name.trim();

      // 1) customers 경로 (메인앱 폼으로 저장된 건)
      const custSnap = await getDocs(query(
        collection(db, 'customers'),
        where('name', '==', nameTrim),
        where('phone', '==', phoneDigits),
      ));
      let latestCust = null;
      custSnap.docs.forEach((d) => {
        const data = { id: d.id, ...d.data() };
        if (!latestCust || (data.created_at?.seconds || 0) > (latestCust.created_at?.seconds || 0)) {
          latestCust = data;
        }
      });

      // 2) service_requests 직접 경로 (랜딩 ApplyFormChat 저장, customers 문서 없음)
      const srDirectSnap = await getDocs(query(
        collection(db, 'service_requests'),
        where('name', '==', nameTrim),
        where('phone', '==', phoneDigits),
      ));

      // 3) customers.id 기반 service_requests
      let rSnapFromCust = { docs: [] };
      if (latestCust) {
        rSnapFromCust = await getDocs(query(
          collection(db, 'service_requests'),
          where('customer_id', '==', latestCust.id),
        ));
      }

      const merged = new Map();
      [...rSnapFromCust.docs, ...srDirectSnap.docs].forEach((d) => {
        if (!merged.has(d.id)) merged.set(d.id, { id: d.id, ...d.data() });
      });

      if (merged.size === 0) {
        setLoading(false);
        return;
      }

      // customer 데이터가 없으면 service_requests 의 첫 문서 기준으로 대체
      const customerFallback = latestCust || (() => {
        const first = Array.from(merged.values())[0];
        return {
          id: first.customer_id || null,
          name: first.name || nameTrim,
          phone: first.phone || phoneDigits,
          moving_address: first.moving_address || '',
          area_pyeong_exact: first.area_pyeong_exact,
          created_at: first.created_at || first.createdAt,
        };
      })();
      setCustomer(customerFallback);
      setRequests(Array.from(merged.values()));
    } catch (err) {
      console.error('조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const completed = requests.filter((r) => r.hq_status === 'completed' || r.hq_status === 'settlement_done');
    if (completed.length === 0) return;
    (async () => {
      const ids = new Set();
      for (const r of completed) {
        const exists = await hasReviewForRequest(r.id);
        if (exists) ids.add(r.id);
      }
      setReviewedIds(ids);
    })();
  }, [requests]);

  const handleReviewDone = () => {
    if (ratingTarget?.id) {
      setReviewedIds((prev) => new Set([...prev, ratingTarget.id]));
    }
    setRatingTarget(null);
  };

  const { activeRequests, cancelledRequests } = useMemo(() => {
    const active = [];
    const cancelled = [];
    requests.forEach((r) => {
      if (r.hq_status === 'cancelled') cancelled.push(r);
      else active.push(r);
    });
    return { activeRequests: active, cancelledRequests: cancelled };
  }, [requests]);

  const openCancelModal = (req) => {
    setCancelTarget(req);
    setCancelReason('');
  };

  const closeCancelModal = () => {
    if (cancelLoading) return;
    setCancelTarget(null);
    setCancelReason('');
  };

  const confirmCancel = async () => {
    if (!cancelTarget || !cancelReason || cancelLoading) return;
    setCancelLoading(true);
    try {
      // Cloud Function 경유로 처리 (비로그인 고객이 Firestore 직접 쓰기 불가 이슈 우회)
      // 서버에서 이름+전화번호 대조 후 service_requests + partner_assignments + complaint_logs 일괄 업데이트
      const res = await fetch('https://us-central1-ieum-8fd99.cloudfunctions.net/cancelServiceRequest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceRequestId: cancelTarget.id,
          cancelReason,
          cancelReasonLabel: CANCEL_REASON_LABEL[cancelReason] || cancelReason,
          customerName: customer?.name || name.trim(),
          customerPhone: (customer?.phone || phone).replace(/\D/g, ''),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || '취소 처리 실패');
      }

      setRequests((prev) => prev.map((r) =>
        r.id === cancelTarget.id
          ? { ...r, hq_status: 'cancelled', cancel_reason: cancelReason, cancel_reason_label: CANCEL_REASON_LABEL[cancelReason], cancelled_at: { seconds: Math.floor(Date.now() / 1000) } }
          : r
      ));
      setCancelTarget(null);
      setCancelReason('');
    } catch (err) {
      console.error('취소 실패:', err);
      alert(`취소 처리 중 오류가 발생했습니다.\n${err.message || ''}`.trim());
    } finally {
      setCancelLoading(false);
    }
  };

  const renderActiveItem = (r) => {
    const st = STATUS_MAP[r.hq_status] || STATUS_MAP.unread;
    const meta = SVC_META[r.category] || { label: r.category, emoji: '📦' };
    const cancellable = r.hq_status === 'unread' || r.hq_status === 'read';
    const isReviewable = (r.hq_status === 'completed' || r.hq_status === 'settlement_done') && !reviewedIds.has(r.id);
    const isReviewed = (r.hq_status === 'completed' || r.hq_status === 'settlement_done') && reviewedIds.has(r.id);
    return (
      <ServiceItem key={r.id}>
        <ServiceIcon>{meta.emoji}</ServiceIcon>
        <ServiceInfo>
          <div className="title-row">
            <span className="svc-name">{meta.label}</span>
            <Badge $color={st.bg} $text={st.text}>{st.label}</Badge>
          </div>
          <div className="status-text">{st.desc}</div>
          {cancellable && (
            <CancelBtn type="button" onClick={() => openCancelModal(r)}>
              <IoCloseCircleOutline size={14} />
              취소하기
            </CancelBtn>
          )}
          {isReviewable && (
            <ReviewBtn
              type="button"
              onClick={() => setRatingTarget({ ...r, customerName: customer?.name, customer_id: customer?.id })}
            >
              <span style={{ color: '#FBBF24', marginRight: 4 }}>★</span>
              평가하기
            </ReviewBtn>
          )}
          {isReviewed && <ReviewedChip>평가 완료</ReviewedChip>}
        </ServiceInfo>
      </ServiceItem>
    );
  };

  const renderCancelledItem = (r) => {
    const meta = SVC_META[r.category] || { label: r.category, emoji: '📦' };
    return (
      <ServiceItem key={r.id}>
        <ServiceIcon>{meta.emoji}</ServiceIcon>
        <ServiceInfo>
          <div className="title-row">
            <span className="svc-name">{meta.label}</span>
            <Badge $color={STATUS_MAP.cancelled.bg} $text={STATUS_MAP.cancelled.text}>취소됨</Badge>
          </div>
          <div className="status-text">
            사유: {r.cancel_reason_label || CANCEL_REASON_LABEL[r.cancel_reason] || r.cancel_reason || '-'}
          </div>
          {r.cancelled_at && (
            <div className="status-text">{formatDateShort(r.cancelled_at)}</div>
          )}
        </ServiceInfo>
      </ServiceItem>
    );
  };

  return (
    <PageOuter>
      <Page>
        <BackHeader>
          <button type="button" onClick={() => { window.location.href = 'https://ieum-landing.web.app'; }}>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 1L1 8l8 7" />
            </svg>
            돌아가기
          </button>
        </BackHeader>

        <Container>
          <Title>마이페이지</Title>
          <SubText>신청 시 입력한 이름과 연락처로 신청 내역과 진행 상태를 확인하세요.</SubText>

          <SearchCard>
            <Field>
              <Label>이름</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="홍길동"
              />
            </Field>
            <Field>
              <Label>연락처</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="01012345678"
              />
            </Field>
            <SearchBtn
              type="button"
              onClick={handleSearch}
              disabled={loading || !name.trim() || !phone.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              {loading ? '조회 중...' : '조회하기'}
            </SearchBtn>
          </SearchCard>

          {searched && !loading && !customer && (
            <EmptyMsg>일치하는 신청 내역이 없습니다.</EmptyMsg>
          )}

          {customer && (
            <>
              <ResultCard>
                <CustomerInfo>
                  <div className="left">
                    <span className="name">{customer.name}</span>
                    <span className="phone">{maskPhone(customer.phone)}</span>
                  </div>
                </CustomerInfo>
                <InfoLine>
                  {customer.created_at && <>신청일: {formatDate(customer.created_at)}<br /></>}
                  {customer.moving_address && <>주소: {customer.moving_address}<br /></>}
                  {customer.area_pyeong_exact && <>평수: {customer.area_pyeong_exact}평</>}
                </InfoLine>
              </ResultCard>

              <AccordionCard>
                <AccordionHeader
                  type="button"
                  onClick={() => setAccordionOpen((v) => !v)}
                >
                  <div>
                    <span className="title">내 신청 내역</span>
                    <span className="count">총 {requests.length}건</span>
                  </div>
                  {accordionOpen ? <IoChevronUp size={18} /> : <IoChevronDown size={18} />}
                </AccordionHeader>

                {accordionOpen && (
                  <>
                    <TabBar>
                      <Tab $active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
                        신청 내역 ({activeRequests.length})
                      </Tab>
                      <Tab $active={activeTab === 'cancelled'} onClick={() => setActiveTab('cancelled')}>
                        취소 이력 ({cancelledRequests.length})
                      </Tab>
                    </TabBar>

                    <TabBody>
                      {activeTab === 'active' && (
                        activeRequests.length === 0
                          ? <EmptyInline>신청 내역이 없습니다.</EmptyInline>
                          : activeRequests.map(renderActiveItem)
                      )}
                      {activeTab === 'cancelled' && (
                        cancelledRequests.length === 0
                          ? <EmptyInline>취소 이력이 없습니다.</EmptyInline>
                          : cancelledRequests.map(renderCancelledItem)
                      )}
                    </TabBody>
                  </>
                )}
              </AccordionCard>
            </>
          )}
        </Container>

        <CtaFooter>
          <NewApplyBtn type="button" onClick={() => navigate('/apply')}>
            <IoAddCircleOutline size={18} />
            새로운 서비스 신청하기
          </NewApplyBtn>
        </CtaFooter>
      </Page>

      {ratingTarget && (
        <RatingModal
          serviceRequest={ratingTarget}
          onClose={() => setRatingTarget(null)}
          onDone={handleReviewDone}
        />
      )}

      {cancelTarget && (
        <ModalOverlay onClick={closeCancelModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalIcon>
              {SVC_META[cancelTarget.category]?.emoji || '📦'}
            </ModalIcon>
            <ModalLabel>취소할 서비스</ModalLabel>
            <ModalServiceName>
              {SVC_META[cancelTarget.category]?.label || cancelTarget.category}
            </ModalServiceName>

            <ReasonLabel>취소 사유를 선택해주세요</ReasonLabel>
            <ReasonGrid>
              {CANCEL_REASONS.map((r) => (
                <ReasonPill
                  key={r.key}
                  type="button"
                  $active={cancelReason === r.key}
                  onClick={() => setCancelReason(r.key)}
                >
                  {r.label}
                </ReasonPill>
              ))}
            </ReasonGrid>

            <WarningBox>
              <IoAlertCircleOutline size={16} />
              <span>취소 후에는 복구할 수 없습니다.</span>
            </WarningBox>

            <ModalActions>
              <ModalGhost type="button" onClick={closeCancelModal} disabled={cancelLoading}>
                돌아가기
              </ModalGhost>
              <ModalDanger
                type="button"
                onClick={confirmCancel}
                disabled={!cancelReason || cancelLoading}
              >
                {cancelLoading ? '처리 중...' : '취소 확정'}
              </ModalDanger>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </PageOuter>
  );
};

export default CustomerMyPage;

const PageOuter = styled.div`
  min-height: 100vh;
  background: #E5E7EB;
  display: flex;
  justify-content: center;
`;

const Page = styled.div`
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  background: #F8FAFC;
  box-shadow: 0 0 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
`;

const BackHeader = styled.div`
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  padding: 18px 20px;

  button {
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: 0;
    font-size: 15px;
    font-weight: 400;
    color: #888;
    cursor: pointer;
    padding: 0;
  }
`;

const Container = styled.div`
  padding: 28px 20px 120px;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 800;
  color: #191F28;
  margin: 0 0 6px;
`;

const SubText = styled.p`
  font-size: 14px;
  color: #8B95A1;
  margin: 0 0 24px;
`;

const SearchCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 24px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #191F28;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  font-size: 15px;
  background: #F8FAFC;
  color: #191F28;

  &:focus {
    outline: none;
    border-color: #4A6CF7;
  }
`;

const SearchBtn = styled.button`
  width: 100%;
  min-height: 52px;
  background: linear-gradient(135deg, #6BA4FF 0%, #4A6CF7 100%);
  color: #fff;
  border: 0;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResultCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .name {
    font-size: 16px;
    font-weight: 700;
    color: #191F28;
  }

  .phone {
    font-size: 14px;
    color: #8B95A1;
  }
`;

const InfoLine = styled.div`
  font-size: 13px;
  color: #4E5968;
  line-height: 1.8;
`;

const AccordionCard = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  background: #fff;
  border: 0;
  cursor: pointer;
  text-align: left;

  .title {
    font-size: 15px;
    font-weight: 700;
    color: #191F28;
    margin-right: 8px;
  }

  .count {
    font-size: 12px;
    color: #6B7280;
  }
`;

const TabBar = styled.div`
  display: flex;
  border-top: 1px solid #F3F4F6;
  border-bottom: 1px solid #F3F4F6;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 0;
  background: ${({ $active }) => ($active ? '#fff' : '#F8FAFC')};
  border: 0;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#4A6CF7' : 'transparent')};
  color: ${({ $active }) => ($active ? '#4A6CF7' : '#6B7280')};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  cursor: pointer;
`;

const TabBody = styled.div`
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
`;

const ServiceIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #EEF2FF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 20px;
`;

const ServiceInfo = styled.div`
  flex: 1;

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .svc-name {
    font-size: 15px;
    font-weight: 700;
    color: #191F28;
  }

  .status-text {
    font-size: 13px;
    color: #8B95A1;
    margin-top: 2px;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color || '#FEF3C7'};
  color: ${({ $text }) => $text || '#92400E'};
`;

const CancelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  color: #EF4444;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0 0;
`;

const ReviewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  margin-top: 6px;
  padding: 6px 12px;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 16px;
  color: #92400E;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #FEF3C7; }
`;

const ReviewedChip = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 6px;
  padding: 5px 10px;
  background: #F1F5F9;
  border-radius: 16px;
  color: #64748B;
  font-size: 11px;
  font-weight: 500;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #8B95A1;
  font-size: 14px;
`;

const EmptyInline = styled.div`
  text-align: center;
  padding: 28px 0;
  color: #9CA3AF;
  font-size: 13px;
`;

const CtaFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border-top: 1px solid #E5E7EB;
  padding: 12px 20px calc(12px + env(safe-area-inset-bottom));
`;

const NewApplyBtn = styled.button`
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #4A6CF7;
  color: #fff;
  border: 0;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #3451D1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 380px;
  background: #fff;
  border-radius: 20px;
  padding: 26px 22px 22px;
  text-align: center;
`;

const ModalIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #EEF2FF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  font-size: 28px;
`;

const ModalLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
`;

const ModalServiceName = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #191F28;
  margin: 4px 0 18px;
`;

const ReasonLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  text-align: left;
  margin-bottom: 10px;
`;

const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
`;

const ReasonPill = styled.button`
  padding: 10px 6px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? '#4A6CF7' : '#E5E7EB')};
  background: ${({ $active }) => ($active ? '#EEF2FF' : '#fff')};
  color: ${({ $active }) => ($active ? '#4A6CF7' : '#1F2937')};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  cursor: pointer;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  background: #FEF2F2;
  color: #B91C1C;
  font-size: 12px;
  font-weight: 600;
  border-radius: 10px;
  margin-bottom: 18px;
`;

const ModalActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const ModalGhost = styled.button`
  min-height: 48px;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  background: #fff;
  color: #1F2937;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ModalDanger = styled.button`
  min-height: 48px;
  border: 0;
  border-radius: 12px;
  background: #EF4444;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
